import { Component, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { REDUCER_TYPES } from '@ptl/state';
import { Dependent, Payload, Spouse, Subscriber } from '../mass-upload/interfaces/submitted-apps.response.interface';
import { TransformedApplication, TransformedDependent, TransformedPayload, TransformedSpouse, TransformedSubscriber } from './interfaces/application.transformed.interface';
import { SubmittedAppsService } from './services/submitted-apps.service';
import { ENTITLEMENTS, EntitlementsHelperService, LINE_OF_BUSINESS_ENUM, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure, USER_ROLE } from "@ptl/account-management";
import { MassUploadGridSubmissionDataService } from '../mass-upload-grid/services/mug-submission-data.service';
//submission steps sep review story
@Component({
    selector: 'app-submission-log',
    templateUrl: './submission-log.component.html',
    styleUrls: ['./submission-log.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SubmissionLogComponent {

  @Input() downloadSubmissionLogPrefix: string;
  @Input() noEntitlementsMessage: string;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('paginator') paginator: MatPaginator;

  displayedColumns: string[] = [
    "arrow",
    'fullName',
    'dob',
    'relationship',
    'appTrackingId',
    'downloadApp'
  ];

  hasEntitlement = false;

  subscriberNameFilter = new FormControl('');
  dobFilter = new FormControl('');
  relationshipFilter = new FormControl('');
  appNumberFilter = new FormControl('');

  filterValues = {
    fullName: '',
    dob: '',
    relationship: '',
    appTrackingId: ''
  }

  dataSource: MatTableDataSource<TransformedApplication>;
  expandedRows: { [key: string]: boolean } = {};
  accordianArrowIcon: string;
  loginId: string;
  fileId: string;

  constructor(
    private submittedAppsService: SubmittedAppsService,
    private reducerUtilityService: ReducerUtilityService,
    private entitlementsHelperService: EntitlementsHelperService,
    private mugSubmissionDataService: MassUploadGridSubmissionDataService
  ) { }

  async ngOnInit() {
    const loginData: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const hasMassEnrollEntitlement = await this.entitlementsHelperService.entitlementValueForSelectedAgencyByNameAndLob(ENTITLEMENTS.MASS_UPLOAD, LINE_OF_BUSINESS_ENUM.INDIVIDUAL, loginData?.userInfo?.role as USER_ROLE);

    if (hasMassEnrollEntitlement) {
      this.hasEntitlement = true;
    } else {
      this.hasEntitlement = false;
    }

    this.getSubmissionLogs();
    this.loginId = this.getProducerLoginInfo();
    this.fileId = this.getFileIdFromUrl();
    this.accordianArrowIcon = "keyboard_arrow_right"

    this.getFilterSubscriptions();
  }

  async checkForEntitlement() {
    return this.entitlementsHelperService.entitlementValueByNameAndLob(ENTITLEMENTS.MASS_UPLOAD, LINE_OF_BUSINESS_ENUM.INDIVIDUAL);
  }

  async getSubmissionLogs() {
    this.fileId = this.getFileIdFromUrl();
    const responseFromService = await this.submittedAppsService.getSuccessSummary(this.fileId);
    const response = responseFromService["payload"];

    const transformedPayload = (payload: Payload): TransformedPayload => {
      return {
        message: payload.message,
        applications: payload.applications.map((application) => {
          return {
            subscriber: transformSubscriber(application.subscriber, application.spouse, application.dependants),
          }
        })
      }
    }

    const transformSubscriber = (subscriber: Subscriber, spouse: Spouse, dependents: Dependent[]): TransformedSubscriber => {
      return {
        fullName: `${subscriber.firstName} ${subscriber.lastName}`,
        dob: subscriber.dob,
        appTrackingId: subscriber.appTrackingId,
        relationship: 'Subscriber',
        spouse: spouse ? transformSpouse(spouse) : null,
        dependents: dependents.map((dependent) => {
          return transformDependent(dependent)
        })
      };
    };

    const transformSpouse = (spouse: Spouse): TransformedSpouse => {
      return {
        fullName: `${spouse.firstName} ${spouse.lastName}`,
        dob: spouse.dob,
        appTrackingId: spouse.appTrackingId,
        relationship: 'Spouse'
      };
    };

    const transformDependent = (dependant: Dependent): TransformedDependent => {
      return {
        fullName: `${dependant.firstName} ${dependant.lastName}`,
        dob: dependant.dob,
        appTrackingId: dependant.appTrackingId,
        relationship: 'Dependent'
      };
    };

    this.transformResponseToDataSource(transformedPayload(response));
  }

  transformResponseToDataSource(data: TransformedPayload) {
    this.dataSource = new MatTableDataSource<TransformedApplication>(data.applications);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.filterPredicate = this.createFilter();
  }

  expand(application: TransformedApplication) {
    this.expandedRows[application.subscriber.appTrackingId] = !this.expandedRows[application.subscriber.appTrackingId];
    if (this.expandedRows[application.subscriber.appTrackingId]) {
      this.accordianArrowIcon = "keyboard_arrow_down"
    } else {
      this.accordianArrowIcon = "keyboard_arrow_right"
    }
  }

  getProducerLoginInfo() {
    const USER_INFO_STATE_NAME = `${REDUCER_TYPES.APPLICATION_STATE}.${LOGIN_INFO_REDUCER_NAME}`;
    const userInfoState: UserInfoStateStructure = this.reducerUtilityService.getGlobalStateItem(USER_INFO_STATE_NAME);
    return userInfoState.userId;
  }

  getFileIdFromUrl() {
    const locationString = window.location.href;
    const fileId = locationString.split('fileId=').pop();
    return fileId;
  }

  getFilterSubscriptions() {
    this.subscriberNameFilter.valueChanges.subscribe(
      (fullName: string) => {
        this.filterValues.fullName = fullName.toLowerCase();
        this.dataSource.filter = JSON.stringify(this.filterValues);
      }
    )
    this.dobFilter.valueChanges.subscribe(
      (dob: string) => {
        this.filterValues.dob = dob.toLowerCase();
        this.dataSource.filter = JSON.stringify(this.filterValues);
      }
    )
    this.relationshipFilter.valueChanges.subscribe(
      (relationship: string) => {
        this.filterValues.relationship = relationship.toLowerCase();
        this.dataSource.filter = JSON.stringify(this.filterValues);
      }
    )
    this.appNumberFilter.valueChanges.subscribe(
      (appTrackingId: string) => {
        this.filterValues.appTrackingId = appTrackingId.toLowerCase();
        this.dataSource.filter = JSON.stringify(this.filterValues);
      }
    )
  }

  createFilter(): (data: TransformedApplication, filter: string) => boolean {
    let filterFunction = function (data: TransformedApplication, filter: string): boolean {
        let searchStrings = JSON.parse(filter);

        let subscriberNameMatches, subscriberDOBMatches, subscriberRelationshipMatches, subscriberAppTrackingIdMatches, spouseNameMatches, spouseDOBMatches, spouseRelationshipMatches, dependentNameMatches, dependentDOBMatches, dependentRelationshipMatches;
        
        let subscriber = data.subscriber;
        if (subscriber) {
            subscriberNameMatches = (subscriber.fullName.toLowerCase().indexOf(searchStrings.fullName) !== -1);
            subscriberDOBMatches = (subscriber.dob.toLowerCase().indexOf(searchStrings.dob) !== -1);
            subscriberRelationshipMatches = (subscriber.relationship.toLowerCase().indexOf(searchStrings.relationship) !== -1);
            subscriberAppTrackingIdMatches = (subscriber.appTrackingId.toLowerCase().indexOf(searchStrings.appTrackingId) !== -1);
        }

        let spouse = data.subscriber.spouse;
        if (spouse) {
            spouseNameMatches = (subscriber.spouse.fullName.toLowerCase().indexOf(searchStrings.fullName) !== -1);
            spouseDOBMatches = (subscriber.spouse.dob.toLowerCase().indexOf(searchStrings.dob) !== -1);
            spouseRelationshipMatches = (subscriber.spouse.relationship.toLowerCase().indexOf(searchStrings.relationship) !== -1);
        }

        let dependent = data.subscriber.dependents && data.subscriber.dependents.length > 0;
        if (dependent) {
           dependentNameMatches = (subscriber.dependents.filter(dep => dep.fullName.toLowerCase().indexOf(searchStrings.fullName) !== -1).length > 0);
           dependentDOBMatches = (subscriber.dependents.filter(dep => dep.dob.toLowerCase().indexOf(searchStrings.dob) !== -1).length > 0);
           dependentRelationshipMatches = (subscriber.dependents.filter(dep => dep.relationship.toLowerCase().indexOf(searchStrings.relationship) !== -1).length > 0);
        }

        const anyNameMatches = (subscriberNameMatches || spouseNameMatches || dependentNameMatches);
        const anyDOBMatches = (subscriberDOBMatches || spouseDOBMatches || dependentDOBMatches);
        const anyRelationshipMatches = (subscriberRelationshipMatches || spouseRelationshipMatches || dependentRelationshipMatches);

        const applicationMatches = anyNameMatches && anyDOBMatches && anyRelationshipMatches && subscriberAppTrackingIdMatches;
    
        return applicationMatches;
    };
    return filterFunction;
  }

  async getLogByFileId(appId: string) {
    await this.mugSubmissionDataService.downloadLogByAppId(appId);
  }
}
 